import React, { useState, useEffect } from 'react';
import { RenewSheet } from "./RenewSheet";
import { DeleteKeyConfirmationSheet } from "./DeleteKeyConfirmationSheet";
import { InsufficientBalanceSheet } from "./InsufficientBalanceSheet";
import { hapticFeedback } from "../utils/telegramSdk";

const API_URL = window.location.origin;

function getTimeRemaining(expireDate) {
    const now = new Date();
    const expiry = new Date(expireDate);

    // Добавляем 3 часа компенсации
    const adjustedExpiry = new Date(expiry.getTime() + (3 * 60 * 60 * 1000));
    const diffMs = adjustedExpiry - now;

    if (diffMs <= 0) {
        return { text: 'Закончился', isExpired: true };
    }

    const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
    const totalDays = Math.floor(totalHours / 24);
    const remainingHours = totalHours % 24;

    if (totalHours < 24) {
        if (totalHours < 1) {
            const minutes = Math.floor(diffMs / (1000 * 60));
            return { text: `Осталось ${minutes} мин`, isExpired: false };
        }
        return { text: `Осталось ${totalHours} ч`, isExpired: false };
    }

    return { text: `Осталось ${totalDays} дн ${remainingHours} ч`, isExpired: false };
}

function KeyCard({ keyData, onAction, index, isDeleting = false, onChangeTariff }) {
    const [timeInfo, setTimeInfo] = useState(getTimeRemaining(keyData.expire_at));
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, index * 100);
        return () => clearTimeout(timer);
    }, [index]);

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeInfo(getTimeRemaining(keyData.expire_at));
        }, 60000);

        return () => clearInterval(timer);
    }, [keyData.expire_at]);

    const keyNumber = keyData.key_number || keyData.uuid.slice(0, 8).toUpperCase();
    const devicesCount = keyData.devices || 1;
    const isTrial = keyData.is_trial;

    const getStatusText = () => {
        if (timeInfo.isExpired) return 'Истек';
        return 'Активный ключ';
    };

    const getExpiryTextColor = () => {
        if (timeInfo.isExpired) return 'expiry-text';
        return 'expiry-text';
    };

    return (
        <article
            className={`card key-card ${isTrial ? 'trial-key' : ''} key-card-animate ${isVisible ? 'visible' : ''} ${isDeleting ? 'deleting' : ''}`}
            style={{ animationDelay: `${index * 0.1}s` }}
        >
            <div className="card-content">
                <div className="key-info">
                    <div className="status-row">
                        <span className="status-text">{getStatusText()}</span>
                        <span className="protocol-text">{keyData.protocol || 'VLESS'}</span>
                    </div>

                    <div className="id-row">
                        <span className="key-id">#{keyNumber}</span>
                        <div className="device-badge">
                            <span>{devicesCount} {devicesCount === 1 ? 'устройство' : 'устройства'}</span>
                        </div>
                    </div>

                    <div className="expiry-info">
                        <span className={getExpiryTextColor()}>
                            {timeInfo.text}
                        </span>
                    </div>
                </div>

                <div className="action-buttons">
                    <div className="row-buttons">
                        <button
                            className="btn btn-outline"
                            onClick={() => onAction(keyData.uuid, 'view')}
                            disabled={isDeleting}
                        >
                            Ваш ключ
                        </button>
                        <button
                            className="btn btn-white"
                            onClick={() => onAction(keyData.uuid, 'renew')}
                            disabled={isDeleting}
                        >
                            Продлить
                        </button>
                    </div>
                    <button
                        className="btn btn-outline"
                        onClick={() => onAction(keyData.uuid, 'changeTariff')}
                        disabled={isDeleting}
                    >
                        Изменить тариф
                    </button>
                    {timeInfo.isExpired && (
                        <button
                            className="btn btn-outline-red"
                            onClick={() => onAction(keyData.uuid, 'delete')}
                            disabled={isDeleting}
                        >
                            {isDeleting ? 'Удаление...' : 'Удалить'}
                        </button>
                    )}
                </div>
            </div>
        </article>
    );
}

function PromoCard({ onBuyClick, isVisible, userKeys }) {
    return (
        <>
            {!userKeys.length ? (
                <article className={`promo-card-empty promo-card-animate ${isVisible ? 'visible' : ''}`}>
                    <div className="promo-content">
                        <div className="promo-text">
                            <h2>Нет активных ключей</h2>
                            <p>Для активации VPN необходимо приобрести ключ</p>
                        </div>
                        <button className="btn btn-green" onClick={onBuyClick}>
                            Купить VPN
                        </button>
                    </div>
                </article>
            ) : (
                <article className={`card promo-card promo-card-animate ${isVisible ? 'visible' : ''}`}>
                    <div className="promo-content">
                        <div className="promo-text">
                            <h2>Нужно больше VPN?</h2>
                            <p>Вы можете приобрести неограниченное количество ключей VPN</p>
                        </div>
                        <button className="btn btn-green" onClick={onBuyClick}>
                            Купить VPN
                        </button>
                    </div>
                </article>
            )}
        </>
    );
}

export function MainPage({ onBuyClick, onViewKey, userKeys = [], loading = false, onKeysUpdate, afterRenew, onGoWallet, onChangeTariff, userId }) {
    const [promoVisible, setPromoVisible] = useState(false);
    const [deletingKeys, setDeletingKeys] = useState({});
    const [renewKey, setRenewKey] = useState(null);
    const [showRenewSheet, setShowRenewSheet] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [keyToDelete, setKeyToDelete] = useState(null);
    const [showInsufficientBalance, setShowInsufficientBalance] = useState(false);
    const [insufficientData, setInsufficientData] = useState({ required: 0, currentBalance: 0 });

    useEffect(() => {
        if (userKeys.length > 0) {
            const timer = setTimeout(() => {
                setPromoVisible(true);
            }, (userKeys.length * 100) + 200);
            return () => clearTimeout(timer);
        } else {
            const timer = setTimeout(() => {
                setPromoVisible(true);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [userKeys]);

    const handleKeyAction = (uuid, action) => {
        if (action === 'view') {
            const key = userKeys.find(k => k.uuid === uuid);
            if (key) {
                onViewKey(key);
            }
        } else if (action === 'delete') {
            const key = userKeys.find(k => k.uuid === uuid);
            setKeyToDelete(key);
            setShowDeleteConfirm(true);
        } else if (action === 'renew') {
            const key = userKeys.find(k => k.uuid === uuid);
            // Убираем проверку баланса здесь - просто открываем шторку продления
            setRenewKey(key);
            setShowRenewSheet(true);
        } else if (action === 'changeTariff') {
            const key = userKeys.find(k => k.uuid === uuid);
            if (key) {
                onChangeTariff(key);
            }
        }
    };

    // Подтверждение удаления
    const handleConfirmDelete = async () => {
        if (!keyToDelete) return;

        setShowDeleteConfirm(false);
        setDeletingKeys(prev => ({ ...prev, [keyToDelete.uuid]: true }));

        try {
            console.log(`🔄 Отправка запроса на удаление ключа ${keyToDelete.uuid}...`);

            const response = await fetch(`${API_URL}/api/key/${keyToDelete.uuid}`, {
                method: 'DELETE'
            });

            const data = await response.json();
            console.log('📥 Ответ сервера:', data);

            if (data.success) {
                if (onKeysUpdate) {
                    await onKeysUpdate();
                } else {
                    window.location.reload();
                }
            } else {
                throw new Error(data.error || 'Unknown error');
            }
        } catch (error) {
            console.error('❌ Ошибка при удалении ключа:', error);
        } finally {
            setDeletingKeys(prev => ({ ...prev, [keyToDelete.uuid]: false }));
            setKeyToDelete(null);
        }
    };

    // Отмена удаления
    const handleCancelDelete = () => {
        setShowDeleteConfirm(false);
        setKeyToDelete(null);
    };

    const handleRenewConfirm = async (months) => {
        if (!renewKey) return;

        try {
            console.log(`🔄 Отправка запроса на продление ключа ${renewKey.uuid} на ${months} месяцев`);

            const response = await fetch(`${API_URL}/api/key/${renewKey.uuid}/renew`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: userId,
                    periodMonths: months
                })
            });

            const data = await response.json();

            if (data.success) {
                // Вызываем afterRenew с UUID ключа
                if (afterRenew) {
                    await afterRenew(renewKey.uuid);
                } else if (onKeysUpdate) {
                    await onKeysUpdate(renewKey.uuid);
                }
            } else {
                if (data.error === 'insufficient_balance') {
                    // Показываем шторку недостатка средств
                    setInsufficientData({
                        required: data.required,
                        currentBalance: data.currentBalance
                    });
                    setShowInsufficientBalance(true);
                } else {
                    console.error('Ошибка продления:', data.error);
                }
            }
        } catch (error) {
            console.error('❌ Ошибка при продлении ключа:', error);
        } finally {
            setShowRenewSheet(false);
            setRenewKey(null);
        }
    };

    // Форматируем имя ключа для отображения в шторке
    const getKeyName = () => {
        if (!keyToDelete) return '';
        return keyToDelete.key_number || keyToDelete.uuid.slice(0, 8).toUpperCase();
    };

    // Закрытие шторки недостатка средств
    const handleCloseInsufficientBalance = () => {
        setShowInsufficientBalance(false);
    };

    // Переход на страницу кошелька
    const handleGoToWallet = () => {
        setShowInsufficientBalance(false);
        window.dispatchEvent(new CustomEvent('navigate-to-wallet'));
    };

    return (
        <div className="mainpage">
            <div className="keys-container">
                <header className="page-header">
                    <h1>Мои ключи</h1>
                </header>

                {userKeys.length > 0 ? (
                    userKeys.map((key, index) => (
                        <KeyCard
                            key={key.uuid}
                            keyData={key}
                            onAction={handleKeyAction}
                            index={index}
                            isDeleting={deletingKeys[key.uuid]}
                            onChangeTariff={onChangeTariff}
                        />
                    ))
                ) : null}

                <PromoCard onBuyClick={onBuyClick} isVisible={promoVisible} userKeys={userKeys} />
            </div>

            <RenewSheet
                isVisible={showRenewSheet}
                onClose={() => {
                    setShowRenewSheet(false);
                    setRenewKey(null);
                }}
                onConfirm={handleRenewConfirm}
                devices={renewKey?.devices || 1}
            />

            {/* Шторка подтверждения удаления */}
            <DeleteKeyConfirmationSheet
                isVisible={showDeleteConfirm}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                keyName={getKeyName()}
            />

            {/* Шторка недостаточно средств */}
            <InsufficientBalanceSheet
                isVisible={showInsufficientBalance}
                onClose={handleCloseInsufficientBalance}
                requiredAmount={insufficientData.required}
                currentBalance={insufficientData.currentBalance}
                onGoWallet={onGoWallet}
            />
        </div>
    );
}
